// src/pages/purchaseOrder/CreatePurchaseOrderPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  X, 
  AlertCircle,
  FileText,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  CreditCard
} from 'lucide-react';
import { createPurchaseOrder } from '../../services/api/purchaseOrder';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import usePurchaseOrderForm from '../../hooks/usePurchaseOrderForm';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import BasicInformationSection from './components/BasicInformationSection';
import PurchaseOrderItemsSection from './components/PurchaseOrderItemsSection';
import FinancialDetailsSection from './components/FinancialDetailsSection';
import PaymentRecordsSection from '../orders/components/PaymentRecordsSection';
import styles from './CreatePurchaseOrderPage.module.css';

const CreatePurchaseOrderPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('pending');
  
  const {
    formData,
    warehouses,
    suppliers,
    paymentMethods,
    isLoading,
    errors,
    totals,
    handleFieldChange,
    addItem,
    updateItem,
    removeItem,
    addSupplier,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    validateForm,
    getPaymentRecordsPayload
  } = usePurchaseOrderForm();

  const handleSubmit = async (status = 'pending') => {
    if (!validateForm()) {
      error(t('purchaseOrder.pleaseFixErrors'), {
        title: t('common.validationError'),
        duration: 5000
      });
      return;
    }

    if (status === 'pending' && !showConfirmDialog) {
      setPendingStatus(status);
      setShowConfirmDialog(true);
      return;
    }

    setIsSubmitting(true);
    setShowConfirmDialog(false);

    try {
      const orderData = {
        warehouseId: formData.warehouseId,
        supplierId: formData.supplierId,
        orderDate: formData.orderDate,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        items: formData.items.map(item => ({
          productId: item.productId,
          variationId: item.variationId,
          quantity: parseInt(item.quantity, 10),
          unitPrice: parseFloat(item.unitPrice),
          notes: item.notes || null
        })),
        extraCharges: parseFloat(formData.extraCharges) || 0,
        extraChargesDescription: formData.extraChargesDescription || null,
        discountAmount: parseFloat(formData.discountAmount) || 0,
        discountDescription: formData.discountDescription || null,
        notes: formData.notes || null,
        status,
        autoReceive: formData.autoReceive,
        paymentRecords: getPaymentRecordsPayload()
      };

      const response = await createPurchaseOrder(orderData);
      
      const successMessage = formData.autoReceive 
        ? t('purchaseOrder.purchaseOrderCreatedAndReceived', { 
            orderNumber: response.purchaseOrder.purchaseOrderNumber 
          })
        : t('purchaseOrder.purchaseOrderCreated', { 
            orderNumber: response.purchaseOrder.purchaseOrderNumber 
          });
      
      success(successMessage, {
        title: t('common.success'),
        duration: 4000
      });
      
      navigate('/inventory/purchase-orders');
    } catch (err) {
      console.error('Purchase order creation error:', err);
      const errorMessage = err.response?.data?.message || t('purchaseOrder.failedToCreate');
      error(errorMessage, {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (formData.items.length > 0 || formData.notes || formData.supplierId) {
      const leave = await confirm(t('purchaseOrder.unsavedChanges'), {
        type: 'warning',
        title: t('common.unsavedChangesTitle'),
        confirmText: t('common.leave'),
        cancelText: t('common.stay'),
      });
      if (leave) {
        navigate('/inventory/purchase-orders');
      }
    } else {
      navigate('/inventory/purchase-orders');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.createPurchaseOrderPage}>
        <PageHeader
          title={t('purchaseOrder.createPurchaseOrder')}
          description={t('purchaseOrder.createPurchaseOrderDescription')}
          icon={Package}
        />

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('purchaseOrder.loadingWarehouses')}</h3>
            <p>{t('purchaseOrder.loadingMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createPurchaseOrderPage}>
      <PageHeader
        title={t('purchaseOrder.createPurchaseOrder')}
        description={t('purchaseOrder.createPurchaseOrderDescription')}
        icon={Package}
        actions={
          <button 
            onClick={handleCancel} 
            className={styles.backButton}
            disabled={isSubmitting}
          >
            <ArrowLeft size={20} />
            {t('purchaseOrder.backToPurchaseOrders')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        <form className={styles.purchaseOrderForm}>
          <div className={styles.formSections}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <FileText className={styles.sectionIcon} size={20} />
                <h2>{t('purchaseOrder.basicInformation')}</h2>
              </div>
              <BasicInformationSection
                formData={formData}
                warehouses={warehouses}
                suppliers={suppliers}
                errors={errors}
                onFieldChange={handleFieldChange}
                onAddSupplier={addSupplier}
              />
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <ShoppingCart className={styles.sectionIcon} size={20} />
                <h2>{t('purchaseOrder.purchaseOrderItems')}</h2>
              </div>
              <PurchaseOrderItemsSection
                items={formData.items}
                errors={errors}
                onAddItem={addItem}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                currency={user?.business?.currency || null}
              />
            </div>
          </div>

          <div className={styles.financialSidebar}>
            <div className={styles.sectionHeader}>
              <DollarSign className={styles.sectionIcon} size={20} />
              <h2>{t('purchaseOrder.financialDetails')}</h2>
            </div>
            <FinancialDetailsSection
              formData={formData}
              totals={totals}
              errors={errors}
              onFieldChange={handleFieldChange}
              currency={user?.business?.currency || null}
              beforeGrandTotal={(
                <div className={styles.sidebarPaymentRecords}>
                  <div className={styles.sidebarSubHeader}>
                    <CreditCard className={styles.sectionIcon} size={18} />
                    <h3>{t('purchaseOrder.paymentRecords')}</h3>
                  </div>
                  <PaymentRecordsSection
                    paymentRecords={formData.paymentRecords}
                    paymentMethods={paymentMethods}
                    orderTotal={totals.grandTotal}
                    errors={errors}
                    required={false}
                    totalLabel={t('purchaseOrder.poTotal')}
                    onAddRecord={addPaymentRecord}
                    onUpdateRecord={updatePaymentRecord}
                    onRemoveRecord={removePaymentRecord}
                  />
                </div>
              )}
            />

            <div className={styles.formActions}>
              <button 
                type="button" 
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                <X size={18} />
                {t('common.cancel')}
              </button>
              {!formData.autoReceive && (
                <button
                  type="button"
                  onClick={() => handleSubmit('draft')}
                  className={styles.draftButton}
                  disabled={isSubmitting}
                >
                  <FileText size={18} />
                  {t('purchaseOrder.saveAsDraft')}
                </button>
              )}
              <button 
                type="button" 
                onClick={() => handleSubmit('pending')}
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className={styles.loadingSpinner}></div>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    {t('purchaseOrder.submitOrder')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {showConfirmDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle className={styles.dialogIcon} size={24} />
              <h3>{t('purchaseOrder.confirmSubmitTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('purchaseOrder.confirmSubmit')}</p>
              {formData.autoReceive && (
                <div className={styles.warningMessage}>
                  <AlertCircle size={16} />
                  <span>{t('purchaseOrder.autoReceiveWarning')}</span>
                </div>
              )}
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                className={styles.dialogCancelButton}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleSubmit(pendingStatus)}
                className={styles.dialogConfirmButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('purchaseOrder.submittingOrder') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePurchaseOrderPage;